/**
 * The publish bar for a vendor card, in one place.
 *
 * The build scripts each carried their own copy of this rule and the vendor
 * form knew nothing about it, so a vendor could fill the form, get approved,
 * see their card live on /sluzby/ — and never reach Google or any AI
 * assistant, with nothing anywhere explaining why. All three live cards in the
 * catalogue failed on this one field. The form now shows the requirement while
 * you type, reading the threshold from here.
 */

/**
 * Minimum description length for a card to be indexed and listed in
 * sitemap.xml / llms.txt. Short cards still render on the site; they are just
 * too thin to publish as a standalone page.
 */
export const VENDOR_DESCRIPTION_MIN = 150;

/** Minimum title length. */
export const VENDOR_TITLE_MIN = 3;

/**
 * Reasons this card cannot be published, in the order a person would fix them.
 * An empty array means it is publishable.
 *
 * @param {object} vendor
 * @param {string} [vendor.title]
 * @param {string} [vendor.location]
 * @param {string} [vendor.description]
 * @param {string[]} [vendor.images] already parsed
 * @returns {string[]}
 */
export function vendorPublishIssues({ title, location, description, images } = {}) {
  const issues = [];

  if (!title || String(title).trim().length < VENDOR_TITLE_MIN) {
    issues.push(`Názov musí mať aspoň ${VENDOR_TITLE_MIN} znaky.`);
  }
  if (!location || String(location).trim().length === 0) {
    issues.push('Vyplňte pôsobnosť (mesto, región alebo celé Slovensko).');
  }
  if (!Array.isArray(images) || images.length === 0) {
    issues.push('Pridajte aspoň jednu fotku.');
  }
  const desc = String(description || '').trim();
  if (desc.length < VENDOR_DESCRIPTION_MIN) {
    issues.push(`Popis musí mať aspoň ${VENDOR_DESCRIPTION_MIN} znakov (má ${desc.length}).`);
  }

  return issues;
}

/**
 * @param {Parameters<typeof vendorPublishIssues>[0]} vendor
 * @returns {boolean}
 */
export function isVendorPublishable(vendor) {
  return vendorPublishIssues(vendor).length === 0;
}

/**
 * Owner id stamped on cards created by scripts/seed/generate-vendors.mjs.
 * Those 270 cards were bulk-generated: an audit (Oct 2026) found most of their
 * websites do not exist, photos are Unsplash stock and phone numbers follow
 * patterns. Until a real business claims or verifies a card, it must not be
 * indexed, listed in sitemap.xml / llms.txt, or show its contact details in
 * crawler-facing HTML — scaled unverified content is a Google spam-policy risk.
 */
export const SEEDED_VENDOR_OWNER = 'system_curated';

/**
 * @param {{owner_id?: string, ownerId?: string}} [vendor]
 * @returns {boolean}
 */
export function isUnverifiedSeedVendor(vendor) {
  const owner = vendor?.owner_id ?? vendor?.ownerId;
  return owner === SEEDED_VENDOR_OWNER;
}
